/**
 * canvas-engine/model/schema.js
 *
 * OWN, self-contained schema constants for the Canvas Engine.
 *
 * ZERO-REUSE CONSTRAINT: this module imports NOTHING from
 * `admin-console-ui/src/modules/template-builder/` or `academy-ui/src/template/`.
 * It defines its own constants from scratch.
 *
 * Pure data only — no React, no DOM, no side effects.
 */

/**
 * Canvas model schema version. The new canvas pipeline emits v2 exclusively.
 * (Req 19.1)
 */
export const CANVAS_SCHEMA_VERSION = 2;

/**
 * Fixed reference width (in px) of the Canvas_Model content frame for v1.
 * All Canvas_Element absolute positions/sizes are defined against this width,
 * and Preview/Reader parity is defined at this width. Responsive/breakpoint
 * behavior is out of scope for v1. (Req 21.1, 21.2, 21.3)
 */
export const REFERENCE_WIDTH = 1120;

/**
 * Default reference height used when a canvas frame omits an explicit height.
 * Not a hard constraint — frames may declare any finite height >= 1.
 * The shared stacking renderer falls back to this height when a section's
 * Canvas_Model height is missing or invalid. (Req 4.9, 5.1)
 */
export const DEFAULT_FRAME_HEIGHT = 680;

/**
 * Whether a canvas frame paints its background edge-to-edge (full viewport
 * width, 0 horizontal inset) by default. Contained sections are the common
 * case, so the default is `false`. (Req 4.3)
 */
export const DEFAULT_FULL_BLEED = false;

/**
 * Default horizontal content inset (in px) applied to a Contained_Section's
 * centered content frame when the frame omits an explicit inset. (Req 4.4)
 */
export const DEFAULT_CONTENT_INSET = 24;

/**
 * Default maximum width (in px) of a contained section's centered content
 * frame. Derived from REFERENCE_WIDTH so contained content stays at the
 * reference width unless a frame overrides it. (Req 4.4, 5.1)
 */
export const DEFAULT_CONTENT_MAX_WIDTH = REFERENCE_WIDTH;

/**
 * Layout mode emitted by the new pipeline. The engine ALWAYS emits "absolute"
 * and never the legacy "native"/"flow" wrap. (design: Data Models)
 */
export const LAYOUT_MODE_ABSOLUTE = 'absolute';

/**
 * The complete set of supported Canvas_Element types. Every element renderer,
 * control set, and decomposition adapter is keyed off this list.
 */
export const ELEMENT_TYPES = Object.freeze([
  'text',
  'image',
  'button',
  'card',
  'grid',
  'imageGrid',
  'html',
  'logo',
  'icon',
  'nav',
  'social',
  'dataBound',
]);

/**
 * The section type produced for every canvas section (uniform capability).
 * Every section — family, chrome, custom, dynamic — is a `customCanvas`
 * section so it exposes the identical Canvas_Option_Set. (Req 3.8, 4.7)
 */
export const CANVAS_SECTION_TYPE = 'customCanvas';

/**
 * Minimum allowed element/frame dimension. Normalization clamps width/height
 * to at least this value. (Req 24.2)
 */
export const MIN_DIMENSION = 1;

/**
 * Opacity bounds. Normalization clamps opacity into this inclusive range.
 * (Req 24.2)
 */
export const MIN_OPACITY = 0;
export const MAX_OPACITY = 1;
